import "firebase/storage";

import { defined, definedMap } from "@glideapps/ts-necessities";

import { isolateAppDescription } from "common/components/SerializedApp";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { updateApp } from "./apps-actions";
import { loadPublishedApp, isUserData, UserData, UserDataAndState } from "common/Database";
import {
    getCurrentAppAndState,
    PublishState,
    OrganizationsState,
    AppAndState,
    getCurrentAppAndStateOrDefaults,
} from "../store/types";
import { GlideAsyncThunk } from "./action-thunk";
import { logError, logInfo } from "common/debug-print";
import { panic, isResponseOK } from "common/Support";
import { isSample } from "common/database-strings";
import { GenerateAppFromDescriptionBody } from "common/firebase-function-types";
import {
    isUnknownFailureBuilderAppResult,
    updateBuilderApp,
    UpdateBuilderAppResultKind,
} from "common/save-builder-app";
import { trackEvent } from "../analytics";
import { upgrade } from "../routes";
import { getBuilderAppDatabase, getUnaffiliatedDatabase } from "../lib/web-database";
import { updateAppSerial } from "./actions";

export function isEditorOfApp(
    appID: string,
    appOwnerUserID: string | undefined,
    userData: UserData,
    organizations: OrganizationsState
): boolean {
    if (appOwnerUserID === undefined) {
        return userData.appIDs.some(a => a === appID) || organizations.some(o => o.appIDs.some(a => a === appID));
    }
    return userData.id === appOwnerUserID || organizations.some(o => o.id === appOwnerUserID);
}

class MissingPublishInformationError extends Error {}

export function publishApp(appID: string, force: boolean, onFinally?: () => void): GlideAsyncThunk {
    function executeOnFinally() {
        if (onFinally !== undefined) {
            onFinally();
        }
    }
    return async (dispatch, getState, history): Promise<void> => {
        function update(newState: PublishState): void {
            logInfo("publish state changed", newState.appURL, newState.publishing, newState.error);
            dispatch(updateApp(appID, { updates: { publishState: newState }, didModify: false }));
        }

        function getCurrentState():
            | { currentAppAndState: AppAndState; userData: UserDataAndState; organizations: OrganizationsState }
            | undefined {
            const { apps, userData, organizations } = getState();
            const currentAppAndState = getCurrentAppAndState(appID, apps);
            if (currentAppAndState === undefined) return undefined;
            return { currentAppAndState, userData, organizations };
        }

        let enteredNetworkLock = false;
        let publishState: PublishState | "loading" | undefined;

        try {
            if (isSample(appID)) {
                return panic("Cannot publish sample apps");
            }

            const db = getBuilderAppDatabase(appID);

            await getUnaffiliatedDatabase().enterNetworkConnectionLock();
            enteredNetworkLock = true;

            const maybeState = getCurrentState();
            if (maybeState === undefined) {
                logError("No app to publish");
                return executeOnFinally();
            }

            const { currentAppAndState, userData, organizations } = maybeState;
            const { userID } = currentAppAndState;
            publishState = currentAppAndState.publishState;

            if (!isUserData(userData) || publishState === "loading") {
                logError("Trying to publish, but not ready", appID, userData, publishState);
                return;
            }

            if (userID === undefined) {
                logError("Undefined userID trying to publish", userData, publishState);
                return;
            }

            if (!isEditorOfApp(appID, userID, userData, organizations)) {
                logInfo("App doesn't belong to current user.  Not publishing.");
                return;
            }

            if ((!publishState.isPublished || !publishState.isPublishingActive) && !force) return;

            if (publishState.publishing === "again") {
                logInfo("Trying to publish, but publishing is already re-requested", appID);
                return;
            }
            if (publishState.publishing === true) {
                logInfo("Already publishing, requesting it again", appID);
                update({ ...publishState, publishing: "again" });
                return;
            }

            if (publishState.appURL === undefined) {
                trackEvent("first publish for app", {
                    app_id: appID,
                });
            }

            logInfo("publishing app", appID);

            const getAppState = () => {
                const lockState = getCurrentAppAndStateOrDefaults(appID, getState().apps);
                if (lockState.serializedApp === undefined || lockState.serial === undefined) {
                    throw new MissingPublishInformationError();
                }
                const appDescription = isolateAppDescription(lockState.serializedApp);
                return { userID: defined(userID), appDescription, serial: lockState.serial };
            };

            const doSerialUpdate = (nextAppID: string, nextSerial: number) => {
                dispatch(updateAppSerial(nextAppID, nextSerial));
            };

            update({ ...publishState, publishing: true, error: undefined });

            const updateResult = await updateBuilderApp(db, appID, getAppState, doSerialUpdate);
            if (
                isUnknownFailureBuilderAppResult(updateResult) &&
                updateResult.exception instanceof MissingPublishInformationError
            ) {
                logError("App state disappeared during update");
                update({
                    ...publishState,
                    publishing: false,
                });
                return;
            } else if (updateResult.kind !== UpdateBuilderAppResultKind.Success) {
                update({
                    ...publishState,
                    publishing: false,
                    error: "Could not save app before publishing",
                });
                return;
            }

            for (;;) {
                const body: GenerateAppFromDescriptionBody = { appID, force };
                const response = await getAppFacilities().callAuthCloudFunction("generateAppFromDescription", body);

                if (!isResponseOK(response)) {
                    if (response?.status === 402) {
                        const r = await response.json();
                        const orgID = r?.upgradeOrganization;
                        if (typeof orgID === "string") {
                            const upgradeURL = upgrade(orgID, false);
                            if (history !== undefined) {
                                history.push(upgradeURL);
                            }
                        }
                    }

                    // conflict
                    if (response?.status === 409) continue;

                    update({
                        ...publishState,
                        publishing: false,
                        error: "There was an error publishing your app.",
                    });
                    return;
                }

                const responseJSON = await response.json();
                const appURL = responseJSON.appURL;

                if (typeof appURL !== "string") {
                    update({
                        ...publishState,
                        publishing: false,
                        error: "Sorry, our publishing service returned a weird response.",
                    });
                    return;
                }

                const maybePublishState = getCurrentState()?.currentAppAndState.publishState;
                if (maybePublishState !== undefined && maybePublishState !== "loading") {
                    publishState = maybePublishState;
                } else {
                    return panic("Publish state went away");
                }

                const publishAgain = publishState.publishing === "again";

                let updates: PublishState = {
                    ...publishState,
                    publishing: publishAgain,
                    isPublished: true,
                    appURL,
                    error: undefined,
                };

                if (!publishState.isPublished) {
                    // If we haven't published before, we should also set
                    // the short name so that it is immediately visible.
                    // FIXME: This is a hack. We shouldn't keep it around.
                    updates = {
                        ...updates,
                        shortName: definedMap(await loadPublishedApp(db, appID), pa => pa.shortName),
                    };
                }

                update(updates);

                if (!publishAgain) {
                    break;
                }
            }
        } catch (e: unknown) {
            logError("Error publishing app", e);
            // FIXME: `publishState` could be out of date here
            if (publishState !== "loading" && publishState !== undefined) {
                update({
                    ...publishState,
                    publishing: false,
                    error: "There was an error publishing your app.",
                });
            }
        } finally {
            if (enteredNetworkLock) {
                getUnaffiliatedDatabase().exitNetworkConnectionLock();
            }
            executeOnFinally();
        }
    };
}
